/**
 * Card-combat enemy roster (HEX-51 / ADR-0015 §4; flattened from
 * `enemies/templates/` in HEX-60 when the V1 `Enemy` registry retired). The five
 * HEX-23 archetypes in the symmetric `Combatant` shape, spanning all four tiers.
 * Adding an enemy is one new `<slug>.ts` template plus one line below.
 *
 * This is the sole enemy registry: world generation (`pickEnemyId`) and the
 * ground latent-encounter picker (`pickLatentEnemy`) both read it.
 */

import type { EnemyTier } from '../types';
import { AVATAR_OF_RUIN } from './avatar-of-ruin';
import { BONE_ARCHER } from './bone-archer';
import { BONEREAPER } from './bonereaper';
import { CAVE_SPIDER } from './cave-spider';
import { EMBERCALLER } from './embercaller';
import { FEN_LURKER } from './fen-lurker';
import { GOBLIN_STRIKER } from './goblin-striker';
import { HEX_WEAVER } from './hex-weaver';
import { MUD_CRAWLER } from './mud-crawler';
import { RIDGEBACK_BRUTE } from './ridgeback-brute';
import { SCRAP_GOBLIN } from './scrap-goblin';
import { SPECTRAL_TYRANT } from './spectral-tyrant';
import { type EnemyTemplate, TIER_DICE, TIER_GRID, buildEnemy } from './template';
import { THE_HOLLOW_KING } from './the-hollow-king';
import { THORN_SPRITE } from './thorn-sprite';
import { TOWER_SHIELD_GUARD } from './tower-shield-guard';
import { VIPER_DUELIST } from './viper-duelist';
import { WARDEN_OF_THORNS } from './warden-of-thorns';
import { WILL_O_WISP } from './will-o-wisp';

export { type EnemyTemplate, TIER_DICE, TIER_GRID, buildEnemy };

export const ENEMY_TEMPLATES: EnemyTemplate[] = [
  // HEX-23 archetypes (HEX-51 / HEX-60)
  GOBLIN_STRIKER,
  CAVE_SPIDER,
  TOWER_SHIELD_GUARD,
  BONE_ARCHER,
  WILL_O_WISP,
  // HEX-83 roster — common
  MUD_CRAWLER,
  FEN_LURKER,
  SCRAP_GOBLIN,
  THORN_SPRITE,
  // HEX-83 roster — rare
  RIDGEBACK_BRUTE,
  VIPER_DUELIST,
  EMBERCALLER,
  // HEX-83 roster — epic
  BONEREAPER,
  HEX_WEAVER,
  WARDEN_OF_THORNS,
  // HEX-83 roster — legendary
  THE_HOLLOW_KING,
  SPECTRAL_TYRANT,
  AVATAR_OF_RUIN,
];

export {
  AVATAR_OF_RUIN,
  BONE_ARCHER,
  BONEREAPER,
  CAVE_SPIDER,
  EMBERCALLER,
  FEN_LURKER,
  GOBLIN_STRIKER,
  HEX_WEAVER,
  MUD_CRAWLER,
  RIDGEBACK_BRUTE,
  SCRAP_GOBLIN,
  SPECTRAL_TYRANT,
  THE_HOLLOW_KING,
  THORN_SPRITE,
  TOWER_SHIELD_GUARD,
  VIPER_DUELIST,
  WARDEN_OF_THORNS,
  WILL_O_WISP,
};

const BY_ID: Map<string, EnemyTemplate> = new Map(ENEMY_TEMPLATES.map((t) => [t.id, t]));

/** All registered template ids, in roster order. (The world-gen picker
 * `ChunkedWorldGen.pickEnemyId` now selects via `TEMPLATES_BY_TIER` + the
 * HEX-64 tier-band table, not this flat list.) */
export const ENEMY_TEMPLATE_IDS: readonly string[] = ENEMY_TEMPLATES.map((t) => t.id);

/** Look up an enemy template by id. Throws on an unknown id — trusted data. */
export function enemyTemplateById(id: string): EnemyTemplate {
  const template = BY_ID.get(id);
  if (template === undefined) throw new Error(`unknown enemy template id: ${id}`);
  return template;
}

/**
 * Best-effort enemy display name for an id, or `null` if unknown (HEX-71).
 * The presentation layer (hover) must not crash on a stale or future enemy id,
 * so unlike `enemyTemplateById` this never throws.
 */
export function tryEnemyName(id: string): string | null {
  return BY_ID.get(id)?.name ?? null;
}

/** Template ids grouped by tier, for the latent-encounter rarity picker
 * (ADR-0012 §5; HEX-40 weights live in `lifecycle/frontierRisk.ts`). */
export const TEMPLATES_BY_TIER: Record<EnemyTier, EnemyTemplate[]> = {
  common: ENEMY_TEMPLATES.filter((t) => t.tier === 'common'),
  rare: ENEMY_TEMPLATES.filter((t) => t.tier === 'rare'),
  epic: ENEMY_TEMPLATES.filter((t) => t.tier === 'epic'),
  legendary: ENEMY_TEMPLATES.filter((t) => t.tier === 'legendary'),
};
